import PredictiveSearch from "@shopify/theme-predictive-search";
import { getSizedImageUrl } from "@/lib/images";
import strip from "string-strip-html";
import { create } from "evx";

import { formatMoney } from "@/neon/currency";

const { strings } = window.theme;

const placeholderSVG = `
  <svg xmlns="http://www.w3.org/2000/svg">
    <rect width="100%" height="100%" fill="transparent"/>
    <svg
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <text
        x="50%"
        y="50%"
        text-anchor="middle"
        alignment-baseline="middle"
        fill="currentColor"
      >
        Abc
      </text>
    </svg>
  </svg>
`;

const defaultOptions = {
  limit: 4,
  show_articles: true,
  show_pages: true,
  show_products: true,
  show_price: true,
  show_vendor: false,
};

export default function ({ opts }) {
  const {
    limit,
    show_articles,
    show_pages,
    show_products,
    show_price,
    show_vendor,
  } = Object.assign(defaultOptions, opts);

  // Create a new event bus
  const { emit, getState, hydrate, on } = create({ count: 0 });

  const types = [
    show_articles && PredictiveSearch.TYPES.ARTICLE,
    show_pages && PredictiveSearch.TYPES.PAGE,
    show_products && PredictiveSearch.TYPES.PRODUCT,
  ].filter(Boolean);

  const instance = new PredictiveSearch({
    resources: {
      type: types,
      limit,
      options: {
        unavailable_products: "last",
        fields: [
          PredictiveSearch.FIELDS.TITLE,
          PredictiveSearch.FIELDS.PRODUCT_TYPE,
          PredictiveSearch.FIELDS.VARIANTS_TITLE,
          PredictiveSearch.FIELDS.VENDOR,
        ],
      },
    },
  });

  on("search", (data, { query }) => instance.query(query));

  instance.on("success", ({ query, resources: { results } }) => {
    hydrate({ query });
    const markup = renderResults(results);

    emit("success", { markup, query });
  });

  instance.on("error", err => {});

  function renderResults(results) {
    const { articles, pages, products } = results;
    let resultsMarkup = "";

    if (products) {
      // Render product results
      resultsMarkup += renderGroup(
        products,
        strings.search.headings.products,
        ({ url, image, title, price, vendor }) => {
          const formattedPrice = formatMoney(price);

          const subheading = [
            show_vendor && vendor,
            show_price && strip(formattedPrice),
          ]
            .filter(Boolean)
            .join(" • ");

          return renderResult({
            url,
            image,
            heading: title,
            subheading,
          });
        }
      );
    }

    if (pages) {
      // Render page results
      resultsMarkup += renderGroup(
        pages,
        strings.search.headings.pages,
        ({ url, image, title, body }) =>
          renderResult({ url, image, heading: title, subheading: strip(body) })
      );
    }

    if (articles) {
      // Render article results
      resultsMarkup += renderGroup(
        articles,
        strings.search.headings.articles,
        ({ url, image, title, body }) =>
          renderResult({ url, image, heading: title, subheading: strip(body) })
      );
    }

    if (resultsMarkup == "") {
      return `
        <div class="quick-search__no-results">
          ${strings.search.no_results}
        </div>
      `;
    }

    const viewAll = `
      <div class="quick-search__view-all accent">
        <button type="submit">${strings.search.view_all} &rarr;</button>
      </div>
    `;

    return resultsMarkup + viewAll;
  }

  function renderGroup(items, heading, cb) {
    const headingMarkup = `<div class="quick-search__header type-heading-meta overline">${heading}</div>`;

    return items.length >= 1 ? headingMarkup + items.map(cb).join("") : "";
  }

  function renderResult({ url, image, heading, subheading }) {
    const img = image
      ? `<img class="image__img lazyload" data-src="${getSizedImageUrl(
          image,
          "120x"
        )}" alt="${heading}" />`
      : placeholderSVG;

    return `
      <a class="quick-search__result" href="${url}">
        <div class="quick-search__result-image">${img}</div>
        <div class="quick-search__result-details">
          <div class="quick-search__result-heading type-body-regular">${heading}</div>
          <div class="quick-search__result-subheading type-body-small">${subheading}</div>
        </div>
      </a>
    `;
  }

  function query(q) {
    emit("search", null, { query: q });
  }

  return { on, query };
}
